import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type {
  Course,
  CourseNode,
  UserCourseProgress,
  UserNodeProgress,
  DailyXp,
} from "@/types/courses";

// ── snake_case → camelCase mappers ──

function mapCourse(row: any): Course {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    author: row.author,
    coverUrl: row.cover_url,
    coverImageUrl: row.cover_image_url,
    iconUrl: row.icon_url,
    iconEmoji: null,
    accentColor: row.accent_color,
    categoryId: row.category_id,
    estimatedHours: row.estimated_hours,
    nodeCount: row.node_count,
    sourceContentIds: row.source_content_ids ?? [],
  };
}

function mapNode(row: any): CourseNode {
  return {
    id: row.id,
    courseId: row.course_id,
    title: row.title,
    description: row.description,
    type: row.type,
    xpReward: row.xp_reward,
    orderIndex: row.order_index,
    prerequisites: row.prerequisites,
    moduleTitle: row.module_title ?? null,
    moduleIndex: row.module_index ?? 0,
  };
}

function mapCourseProgress(row: any): UserCourseProgress {
  return {
    id: row.id,
    userId: row.user_id,
    courseId: row.course_id,
    enrolledAt: row.enrolled_at,
    completedNodes: row.completed_nodes,
    totalXp: row.total_xp,
    lastActiveAt: row.last_active_at,
  };
}

function mapNodeProgress(row: any): UserNodeProgress {
  return {
    id: row.id,
    userId: row.user_id,
    nodeId: row.node_id,
    courseId: row.course_id,
    status: row.status,
    xpEarned: row.xp_earned,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    currentStepIndex: row.current_step_index,
  };
}

// ── Hook ──

export function useCourseData(courseId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id;

  // Query 1: Course
  const courseQuery = useQuery({
    queryKey: ["course", courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .eq("id", courseId!)
        .single();
      if (error) throw error;
      return mapCourse(data);
    },
    enabled: !!courseId,
  });

  // Query 2: Nodes
  const nodesQuery = useQuery({
    queryKey: ["course-nodes", courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_nodes")
        .select("*")
        .eq("course_id", courseId!)
        .order("order_index", { ascending: true });
      if (error) throw error;
      return (data ?? []).map(mapNode);
    },
    enabled: !!courseId,
  });

  // Query 3: Content step counts (for progress fraction)
  const nodeIds = nodesQuery.data?.map((n) => n.id) ?? [];
  const contentCountsQuery = useQuery({
    queryKey: ["course-content-counts", courseId, nodeIds],
    queryFn: async () => {
      if (nodeIds.length === 0) return new Map<string, number>();
      const { data, error } = await supabase
        .from("course_node_content")
        .select("node_id")
        .in("node_id", nodeIds);
      if (error) throw error;
      // Count steps per node
      const counts = new Map<string, number>();
      for (const row of data ?? []) {
        counts.set(row.node_id, (counts.get(row.node_id) ?? 0) + 1);
      }
      return counts;
    },
    enabled: nodeIds.length > 0,
  });

  // Query 4: User course progress
  const courseProgressQuery = useQuery({
    queryKey: ["user-course-progress", userId, courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_course_progress")
        .select("*")
        .eq("user_id", userId!)
        .eq("course_id", courseId!)
        .maybeSingle();
      if (error) throw error;
      return data ? mapCourseProgress(data) : null;
    },
    enabled: !!userId && !!courseId,
  });

  // Query 5: User node progress (all nodes at once)
  const nodeProgressQuery = useQuery({
    queryKey: ["user-node-progress", userId, courseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_node_progress")
        .select("*")
        .eq("user_id", userId!)
        .eq("course_id", courseId!);
      if (error) throw error;
      const mapped = (data ?? []).map(mapNodeProgress);
      // Return as a Map keyed by nodeId for fast lookup
      const map = new Map<string, UserNodeProgress>();
      for (const np of mapped) {
        map.set(np.nodeId, np);
      }
      return map;
    },
    enabled: !!userId && !!courseId,
  });

  // Query 6: Weekly XP (last 7 days)
  const weeklyXpQuery = useQuery({
    queryKey: ["weekly-xp", userId, courseId],
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from("xp_events")
        .select("xp_amount, created_at")
        .eq("user_id", userId!)
        .eq("course_id", courseId!)
        .gte("created_at", sevenDaysAgo.toISOString());
      if (error) throw error;

      // Group by date
      const xpByDate = new Map<string, number>();
      for (const row of data ?? []) {
        const date = row.created_at.split("T")[0];
        xpByDate.set(date, (xpByDate.get(date) ?? 0) + row.xp_amount);
      }

      // Build 7-day array
      const result: DailyXp[] = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split("T")[0];
        result.push({ date: dateStr, xp: xpByDate.get(dateStr) ?? 0 });
      }
      return result;
    },
    enabled: !!userId && !!courseId,
  });

  const isLoading =
    courseQuery.isLoading ||
    nodesQuery.isLoading ||
    (nodeIds.length > 0 && contentCountsQuery.isLoading) ||
    (!!userId && courseProgressQuery.isLoading) ||
    (!!userId && nodeProgressQuery.isLoading) ||
    (!!userId && weeklyXpQuery.isLoading);

  const isError = courseQuery.isError;

  return {
    course: courseQuery.data ?? null,
    nodes: nodesQuery.data ?? [],
    contentStepCounts: contentCountsQuery.data ?? new Map<string, number>(),
    courseProgress: courseProgressQuery.data ?? null,
    nodeProgressMap: nodeProgressQuery.data ?? new Map<string, UserNodeProgress>(),
    weeklyXp: weeklyXpQuery.data ?? [],
    isLoading,
    isError,
    userId,
  };
}
